import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Lock, Play, RotateCcw } from 'lucide-react';
import type { CourseProgress, EnrolmentWithCourse } from '@formency/shared';
import {
  useCourseProgress,
  useCourseWithContent,
  useMyEnrolments,
  type CourseWithContent,
  type LessonWithContent,
  type ModuleWithContent,
  type TopicWithContent,
} from '@/shared/api/hooks';
import { PageContainer } from '@/interfaces/learner/ui';
import { filterPublishedModules } from '@/features/learner/course/courseContent';

type StopState = CourseProgress['lessons'][number]['status'];

interface LessonStop {
  lesson: LessonWithContent;
  module: ModuleWithContent;
  topic: TopicWithContent;
  order: number;
  status: StopState;
  scorePct: number | null;
}

interface TopicStopGroup {
  topic: TopicWithContent;
  stops: LessonStop[];
}

function sortForRecent(enrolments: EnrolmentWithCourse[]): EnrolmentWithCourse[] {
  return [...enrolments].sort((a, b) => {
    const aTime = new Date(
      a.lastAccessedAt ?? a.startedAt ?? a.enrolledAt ?? a.createdAt ?? 0
    ).getTime();
    const bTime = new Date(
      b.lastAccessedAt ?? b.startedAt ?? b.enrolledAt ?? b.createdAt ?? 0
    ).getTime();
    return bTime - aTime;
  });
}

function flattenLessons(
  course: CourseWithContent | undefined,
  progress: CourseProgress | undefined
) {
  const progressMap = new Map(
    progress?.lessons.map((lessonProgress) => [lessonProgress.lessonId, lessonProgress])
  );
  const hasProgressData = progressMap.size > 0;
  const stops: LessonStop[] = [];
  let fallbackAvailableUsed = false;

  for (const module of filterPublishedModules(course?.modules ?? [])) {
    for (const topic of module.topics ?? []) {
      for (const lesson of topic.lessons ?? []) {
        const lessonProgress = progressMap.get(lesson.id);
        let status: StopState;

        if (lessonProgress) {
          status = lessonProgress.status;
        } else if (!hasProgressData && !fallbackAvailableUsed) {
          status = 'available';
          fallbackAvailableUsed = true;
        } else {
          status = 'locked';
        }

        stops.push({
          lesson,
          module,
          topic,
          order: stops.length + 1,
          status,
          scorePct: lessonProgress?.scorePct ?? null,
        });
      }
    }
  }

  return stops;
}

function firstActionableStop(stops: LessonStop[]): LessonStop | undefined {
  return (
    stops.find((stop) => stop.status === 'in-progress') ??
    stops.find((stop) => stop.status === 'available') ??
    stops.find((stop) => stop.status === 'failed')
  );
}

function moduleAnchorStop(stops: LessonStop[]): LessonStop | undefined {
  return (
    firstActionableStop(stops) ??
    [...stops].reverse().find((stop) => stop.status === 'completed') ??
    stops[0]
  );
}

function groupStopsByTopic(stops: LessonStop[]): TopicStopGroup[] {
  const groups: TopicStopGroup[] = [];

  for (const stop of stops) {
    const current = groups[groups.length - 1];
    if (current?.topic.id === stop.topic.id) {
      current.stops.push(stop);
    } else {
      groups.push({ topic: stop.topic, stops: [stop] });
    }
  }

  return groups;
}

function statusIcon(status: StopState) {
  if (status === 'completed') return <Check size={13} strokeWidth={2.6} />;
  if (status === 'in-progress') return <Play size={12} fill="currentColor" strokeWidth={2.5} />;
  if (status === 'failed') return <RotateCcw size={13} strokeWidth={2.5} />;
  if (status === 'locked') return <Lock size={12} strokeWidth={2.4} />;
  return null;
}

function statusLabel(stop: LessonStop): string {
  if (stop.status === 'completed' && stop.scorePct != null) return `${Math.round(stop.scorePct)}%`;
  if (stop.status === 'completed') return 'Done';
  if (stop.status === 'in-progress') return 'Resume';
  if (stop.status === 'failed') return 'Retry';
  if (stop.status === 'available') return 'Start';
  return 'Locked';
}

function CourseLineSkeleton() {
  return (
    <section
      className="real-course-line real-course-line--loading"
      aria-label="Loading course line"
    >
      <div className="loading-block loading-block--title" />
      <div className="loading-track">
        {Array.from({ length: 7 }).map((_, index) => (
          <span key={index} />
        ))}
      </div>
    </section>
  );
}

function RealCourseLine({
  course,
  progress,
}: {
  course: CourseWithContent;
  progress: CourseProgress | undefined;
}) {
  const allStops = useMemo(() => flattenLessons(course, progress), [course, progress]);
  const anchorStop = moduleAnchorStop(allStops);
  const moduleStops = anchorStop
    ? allStops.filter((stop) => stop.module.id === anchorStop.module.id)
    : allStops;
  const topicGroups = useMemo(() => groupStopsByTopic(moduleStops), [moduleStops]);
  const nextStop = firstActionableStop(moduleStops) ?? firstActionableStop(allStops);
  const totalLessons = progress?.totalLessons ?? allStops.length;
  const completedLessons =
    progress?.completedLessons ?? allStops.filter((stop) => stop.status === 'completed').length;
  const percentage =
    progress?.percentage ??
    (totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0);
  const accent = course.accentColour ?? 'var(--color-accent)';
  const nextHref =
    nextStop && nextStop.status !== 'locked'
      ? `/courses/${course.id}/${nextStop.lesson.id}`
      : `/courses/${course.id}`;

  if (allStops.length === 0) {
    return (
      <section className="real-course-line">
        <p className="empty-copy">No published lessons are available for this course yet.</p>
      </section>
    );
  }

  return (
    <section
      className="real-course-line"
      style={{ '--course-accent': accent } as React.CSSProperties}
    >
      <div className="line-heading">
        <div className="line-heading__course">
          <p>Current module - {anchorStop?.module.title ?? 'Course path'}</p>
          <h1>{course.title}</h1>
        </div>

        <div className="line-heading__completion" aria-label={`Course ${percentage}% complete`}>
          <strong>{percentage}%</strong>
          <span>complete</span>
        </div>
      </div>

      <div className="line-track-wrap">
        <div
          className="line-track"
          role="list"
          aria-label={`${anchorStop?.module.title ?? course.title} module route`}
        >
          {topicGroups.map((group, groupIndex) => {
            const stopsBeforeGroup = topicGroups
              .slice(0, groupIndex)
              .reduce((total, previous) => total + previous.stops.length, 0);

            return (
              <section className="line-topic-group" key={group.topic.id} role="listitem">
                <div className="line-topic-label">{group.topic.title}</div>
                <ol className="line-topic-stops">
                  {group.stops.map((stop, index) => {
                    const stopIndex = stopsBeforeGroup + index;
                    const href =
                      stop.status === 'locked'
                        ? `/courses/${course.id}`
                        : `/courses/${course.id}/${stop.lesson.id}`;
                    return (
                      <li className={`line-stop line-stop--${stop.status}`} key={stop.lesson.id}>
                        <Link
                          to={href}
                          className="line-stop__marker"
                          aria-label={`${stop.lesson.title}: ${statusLabel(stop)}`}
                        >
                          {statusIcon(stop.status) ?? (
                            <span className="line-stop__order">{stopIndex + 1}</span>
                          )}
                        </Link>
                        <span className="line-stop__label">{stop.lesson.title}</span>
                        {stop.status !== 'locked' &&
                          stop.status !== 'available' &&
                          stop.status !== 'in-progress' && (
                            <span className="line-stop__meta">{statusLabel(stop)}</span>
                          )}
                      </li>
                    );
                  })}
                </ol>
              </section>
            );
          })}
        </div>
      </div>

      <div className="line-footer">
        <div>
          <p>{nextStop ? 'Next in module' : 'Module complete'}</p>
          <strong>{nextStop?.lesson.title ?? anchorStop?.module.title ?? 'Open course'}</strong>
          {nextStop && <span>{nextStop.topic.title}</span>}
        </div>

        <Link to={nextHref} className="line-action">
          {nextStop?.status === 'in-progress'
            ? 'Resume'
            : nextStop?.status === 'failed'
              ? 'Retry'
              : nextStop
                ? 'Start'
                : 'Open course'}
          <ArrowRight size={16} strokeWidth={2.4} />
        </Link>
      </div>
    </section>
  );
}

export default function LearnerHomeConcepts() {
  const { data: enrolments, isLoading: loadingEnrolments } = useMyEnrolments();
  const selectedEnrolment = useMemo(
    () => (enrolments?.length ? sortForRecent(enrolments)[0] : undefined),
    [enrolments]
  );
  const courseId = selectedEnrolment?.courseId;
  const { data: course, isLoading: loadingCourse } = useCourseWithContent(courseId);
  const { data: progress, isLoading: loadingProgress } = useCourseProgress(courseId);
  const isLoading = loadingEnrolments || loadingCourse || loadingProgress;

  return (
    <PageContainer
      className="home-lab-page"
      style={{
        paddingTop: 'var(--page-top-spacing)',
        paddingBottom: 96,
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div className="home-lab-shell">
        <div className="home-lab-kicker">
          <span>Real course trail</span>
          <p>Most recently active enrolled course, rendered from live hierarchy and progress.</p>
        </div>

        {isLoading && <CourseLineSkeleton />}
        {!isLoading && selectedEnrolment && course && (
          <RealCourseLine course={course} progress={progress} />
        )}
        {!isLoading && !selectedEnrolment && (
          <section className="real-course-line">
            <p className="empty-copy">No enrolled courses found.</p>
          </section>
        )}
      </div>

      <style>{`
        .home-lab-shell {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
          width: min(100%, 760px);
        }

        .home-lab-kicker {
          display: grid;
          gap: var(--space-4);
          padding: 0 2px;
        }

        .home-lab-kicker span {
          font-size: var(--type-eyebrow-size);
          font-weight: var(--type-eyebrow-weight);
          letter-spacing: var(--type-eyebrow-tracking);
          line-height: var(--type-eyebrow-leading);
          text-transform: uppercase;
          color: var(--color-text-muted);
        }

        .home-lab-kicker p {
          margin: 0;
          max-width: 520px;
          color: var(--color-text-muted);
          font-size: var(--type-meta-size);
          line-height: var(--leading-normal);
        }

        .real-course-line {
          --line-card-padding: clamp(18px, 2.2vw, 24px);
          position: relative;
          overflow: hidden;
          min-height: 0;
          display: grid;
          gap: var(--space-4);
          padding: var(--line-card-padding);
          border-radius: var(--dashboard-panel-radius);
          border: 1px solid color-mix(in srgb, var(--course-accent, var(--color-accent)) 26%, var(--color-surface-border));
          background: var(--color-surface);
          backdrop-filter: blur(16px) saturate(1.3);
          -webkit-backdrop-filter: blur(16px) saturate(1.3);
          box-shadow: var(--surface-shadow-glass-rest);
        }

        .line-heading {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: var(--space-4);
          align-items: start;
        }

        .line-heading__course {
          min-width: 0;
        }

        .line-heading__course p,
        .line-footer p {
          margin: 0;
          color: var(--course-accent, var(--color-accent));
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
          line-height: var(--leading-snug);
        }

        .line-heading__course h1 {
          margin: 8px 0 0;
          max-width: 22ch;
          color: var(--color-text);
          font-family: var(--font-display);
          font-size: clamp(1.55rem, 2.8vw, 2.25rem);
          font-weight: var(--weight-semibold);
          letter-spacing: var(--tracking-normal);
          line-height: var(--leading-tight);
        }

        .line-heading__course span,
        .line-footer span {
          display: block;
          margin-top: 6px;
          color: var(--color-text-secondary);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-medium);
          line-height: var(--leading-snug);
        }

        .line-heading__completion {
          display: flex;
          align-items: baseline;
          gap: 6px;
          margin-top: 10px;
          color: var(--color-text-muted);
          text-align: right;
          white-space: nowrap;
        }

        .line-heading__completion strong {
          color: var(--color-text);
          font-family: var(--font-display);
          font-size: var(--text-lg);
          font-weight: var(--weight-bold);
          line-height: 1;
        }

        .line-heading__completion span {
          color: var(--color-text-muted);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
        }

        .line-track-wrap {
          min-width: 0;
          display: block;
          overflow-x: auto;
          margin-inline: calc(var(--line-card-padding) * -1);
          padding: 6px var(--line-card-padding) 2px;
          scrollbar-width: thin;
        }

        .line-track {
          --stop-size: clamp(108px, 24vw, 136px);
          position: relative;
          display: flex;
          align-items: stretch;
          width: max-content;
          min-width: 100%;
          margin: 0;
          padding: 0;
        }

        .line-track::before {
          content: '';
          position: absolute;
          top: 35px;
          left: calc(var(--line-card-padding) * -1);
          right: calc(var(--line-card-padding) * -1);
          height: 3px;
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--course-accent, var(--color-accent)) 30%, var(--color-surface-border));
          pointer-events: none;
        }

        .line-topic-group {
          position: relative;
          display: grid;
          grid-template-rows: 24px 1fr;
          min-width: max-content;
        }

        .line-topic-label {
          position: sticky;
          left: 0;
          z-index: 2;
          justify-self: start;
          width: var(--stop-size);
          min-height: 18px;
          max-width: var(--stop-size);
          padding: 0 6px;
          box-sizing: border-box;
          color: var(--color-text-muted);
          font-size: 11px;
          font-weight: var(--weight-semibold);
          line-height: 1.25;
          text-align: center;
          overflow-wrap: anywhere;
          text-wrap: balance;
          pointer-events: none;
        }

        .line-topic-stops {
          display: grid;
          grid-auto-flow: column;
          grid-auto-columns: var(--stop-size);
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .line-stop {
          position: relative;
          display: grid;
          justify-items: center;
          gap: 6px;
          min-width: 0;
          text-align: center;
        }

        .line-stop__marker {
          position: relative;
          z-index: 1;
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: var(--radius-full);
          border: 2px solid color-mix(in srgb, var(--course-accent, var(--color-accent)) 42%, var(--color-border));
          background: color-mix(in srgb, var(--color-surface-solid) 88%, transparent);
          color: var(--course-accent, var(--color-accent));
          text-decoration: none;
          box-shadow: inset 0 0.5px 0 var(--color-surface-highlight);
          transition:
            transform var(--motion-fast) var(--motion-ease-out),
            border-color var(--motion-fast) var(--motion-ease-out),
            background var(--motion-fast) var(--motion-ease-out);
        }

        .line-stop__marker:hover {
          transform: scale(1.05);
          border-color: var(--course-accent, var(--color-accent));
        }

        .line-stop--completed .line-stop__marker,
        .line-stop--in-progress .line-stop__marker,
        .line-stop--failed .line-stop__marker {
          background: var(--course-accent, var(--color-accent));
          color: var(--color-text-on-accent);
        }

        .line-stop--failed .line-stop__marker {
          background: var(--color-warning);
          border-color: var(--color-warning);
        }

        .line-stop--locked {
          opacity: 0.72;
        }

        .line-stop--locked .line-stop__marker {
          background: color-mix(in srgb, var(--color-surface-solid) 96%, var(--course-accent, var(--color-accent)));
          border-color: color-mix(in srgb, var(--course-accent, var(--color-accent)) 24%, var(--color-border));
          color: color-mix(in srgb, var(--course-accent, var(--color-accent)) 64%, var(--color-text-muted));
          cursor: default;
        }

        .line-stop__order {
          font-size: var(--type-meta-size);
          font-weight: var(--weight-bold);
          line-height: 1;
        }

        .line-stop__label {
          display: block;
          width: calc(var(--stop-size) - 12px);
          max-width: calc(var(--stop-size) - 12px);
          color: var(--color-text-secondary);
          font-size: 12px;
          font-weight: var(--weight-semibold);
          line-height: var(--leading-snug);
          overflow-wrap: anywhere;
          text-wrap: balance;
          white-space: normal;
        }

        .line-stop--in-progress .line-stop__label,
        .line-stop--available .line-stop__label {
          color: var(--color-text);
        }

        .line-stop__meta {
          color: var(--color-text-muted);
          font-size: 11px;
          font-weight: var(--weight-medium);
          line-height: 1.2;
        }

        .line-footer {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: var(--space-4);
          align-items: end;
          padding-top: var(--space-3);
          border-top: 1px solid color-mix(in srgb, var(--color-surface-border) 74%, transparent);
        }

        .line-footer strong {
          display: block;
          margin-top: 4px;
          color: var(--color-text);
          font-family: var(--font-display);
          font-size: var(--text-base);
          font-weight: var(--weight-semibold);
          line-height: var(--leading-snug);
        }

        .line-action {
          min-height: 42px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 0 16px;
          border-radius: var(--radius-lg);
          background: var(--course-accent, var(--color-accent));
          color: var(--color-text-on-accent);
          font-size: var(--type-meta-size);
          font-weight: var(--weight-semibold);
          text-decoration: none;
          box-shadow: var(--shadow-accent);
          transition:
            transform var(--motion-fast) var(--motion-ease-out),
            box-shadow var(--motion-fast) var(--motion-ease-out);
        }

        .line-action:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 22px color-mix(in srgb, var(--course-accent, var(--color-accent)) 24%, transparent);
        }

        .empty-copy {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: var(--type-body-size);
        }

        .real-course-line--loading {
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 260px;
        }

        .loading-block,
        .loading-track span {
          border-radius: var(--radius-full);
          background: color-mix(in srgb, var(--color-text-muted) 12%, transparent);
          animation: pulse 1.3s ease-in-out infinite;
        }

        .loading-block--title {
          width: min(420px, 70%);
          height: 42px;
        }

        .loading-track {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 18px;
          margin-top: 46px;
        }

        .loading-track span {
          height: 42px;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.45; }
          50% { opacity: 0.88; }
        }

        @media (max-width: 820px) {
          .line-heading,
          .line-footer {
            grid-template-columns: 1fr;
          }

          .line-heading__completion {
            width: fit-content;
            text-align: left;
          }

          .line-track {
            --stop-size: 108px;
          }

          .line-action {
            width: 100%;
          }
        }
      `}</style>
    </PageContainer>
  );
}
